
import React, { useState, useEffect } from 'react';
import { cn } from "@/lib/utils";
import { Check, ChevronDown, ChevronUp, Copy } from 'lucide-react';
import { ActionCardProps } from '@/types/action-card';
import { motion, AnimatePresence } from 'framer-motion';
import { Card } from "@/components/ui/card";
import { toast } from "sonner";

export const GlassActionCard = ({ data, className }: ActionCardProps) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [showSuccessGlow, setShowSuccessGlow] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setShowSuccessGlow(true);
    const timer = setTimeout(() => {
      setShowSuccessGlow(false);
    }, 2000);
    return () => clearTimeout(timer);
  }, [data.status]);

  const handleCopyPayload = () => {
    if (data.payload) {
      navigator.clipboard.writeText(JSON.stringify(data.payload, null, 2));
      setCopied(true);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const statusConfig = {
    success: {
      gradient: "from-emerald-500/20 to-emerald-500/5",
      glow: "shadow-[0_0_25px_-5px_rgba(16,185,129,0.3)]",
      pulseColor: "emerald",
      icon: <Check className="h-4 w-4 text-emerald-500" />
    },
    error: {
      gradient: "from-red-500/20 to-red-500/5",
      glow: "shadow-[0_0_25px_-5px_rgba(239,68,68,0.3)]",
      pulseColor: "red",
      icon: <Check className="h-4 w-4 text-red-500" />
    },
    pending: {
      gradient: "from-primary/20 to-primary/5",
      glow: "shadow-[0_0_25px_-5px_rgba(139,92,246,0.3)]",
      pulseColor: "violet",
      icon: <Check className="h-4 w-4 text-primary" />
    }
  };

  const config = statusConfig[data.status];

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ 
        type: "spring",
        stiffness: 500,
        damping: 30
      }}
      className={cn(
        "relative",
        // Removed the animate-soft-float class that was causing the continuous bouncing
        config.glow,
        "hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300",
        className
      )}
    >
      <Card className={cn(
        "backdrop-blur-sm bg-white/10 dark:bg-gray-900/20",
        "border border-white/20 dark:border-gray-800/30",
        "bg-gradient-to-br",
        config.gradient,
        "shadow-lg",
        showSuccessGlow && `animate-pulse-${config.pulseColor}`,
        "transition-all duration-300",
        "p-4"
      )}>
        {/* Header */}
        <div className="flex items-center justify-between pb-3 border-b border-white/10 dark:border-gray-800/50">
          <div className="flex items-center gap-3">
            <motion.div 
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className={cn(
                "relative flex h-8 w-8 items-center justify-center rounded-lg bg-white/20 dark:bg-gray-800/30",
                showSuccessGlow && "animate-scale-check"
              )}
            >
              {config.icon}
            </motion.div>
            <div className="space-y-0.5">
              <h3 className="text-lg font-semibold tracking-tight">{data.title}</h3>
              <p className="text-sm text-muted-foreground">{data.summary}</p>
            </div>
          </div>
          
          <div className="flex gap-2">
            {data.payload && (
              <button
                onClick={handleCopyPayload}
                className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-white/10 dark:hover:bg-gray-800/50 transition-colors"
                aria-label="Copy payload"
              >
                {copied ? (
                  <motion.div
                    initial={{ scale: 0.8 }}
                    animate={{ scale: 1 }}
                    className="text-emerald-500"
                  >
                    <Check className="h-4 w-4" />
                  </motion.div>
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </button>
            )}
            
            {data.isCollapsible && (
              <button
                onClick={() => setIsCollapsed(!isCollapsed)}
                className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-white/10 dark:hover:bg-gray-800/50 transition-colors"
                aria-label={isCollapsed ? "Expand card" : "Collapse card"}
              >
                {isCollapsed ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronUp className="h-4 w-4" />
                )}
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        <AnimatePresence>
          {!isCollapsed && data.payload && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <div className="py-4 space-y-2">
                {Object.entries(data.payload).map(([key, value], index) => (
                  <motion.div
                    key={key}
                    initial={{ opacity: 0, y: 5 }}
                    animate={{ 
                      opacity: 1, 
                      y: 0,
                      transition: { delay: index * 0.05 }
                    }}
                    className={cn(
                      "flex items-center justify-between py-2",
                      index !== 0 && "border-t border-white/10 dark:border-gray-800/50"
                    )}
                  >
                    <span className="font-mono text-sm text-muted-foreground">{key}</span>
                    <span className="font-medium px-2 py-1 rounded bg-white/5 dark:bg-gray-800/50 max-w-[180px] truncate">
                      {String(value)}
                    </span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Timestamp */}
        <div className="pt-2 text-[10px] text-muted-foreground">
          {data.timestamp}
        </div>
      </Card>
    </motion.div>
  );
};
